import React, { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  MoreVerticalIcon,
  PlusIcon,
  RepeatIcon,
} from "lucide-react";
import { StatusBadge } from "../components/tuition/StatusBadge";
import { PositionForm } from "../components/payroll/PositionForm";
import { StipendForm } from "../components/payroll/StipendForm";
import { AdjustmentForm } from "../components/payroll/AdjustmentForm";
import { TimeEntryGrid } from "../components/payroll/TimeEntryGrid";
import { ExternalTuitionPaymentForm } from "../components/payroll/ExternalTuitionPaymentForm";
import { DeductionForm } from "../components/payroll/DeductionForm";
import { LeaveManagementModal } from "../components/payroll/LeaveManagementModal";
import type {
  PayrollAdjustmentDraft,
  PayrollDeductionDraft,
  PayrollEmployeeDetailPageProps,
  PayrollExternalTuitionDraft,
  PayrollPositionDraft,
  PayrollStipendDraft,
  PayrollUiAdjustment,
  PayrollUiDeduction,
  PayrollUiEmployee,
  PayrollUiExternalTuitionPayment,
  PayrollUiPosition,
  PayrollUiStipend,
} from "../types";

type DetailTab =
  | "overview"
  | "positions"
  | "stipends"
  | "time"
  | "adjustments"
  | "external-tuition"
  | "deductions";

const DETAIL_TABS: Array<{ id: DetailTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "positions", label: "Positions" },
  { id: "stipends", label: "Stipends" },
  { id: "time", label: "Time Entry" },
  { id: "adjustments", label: "Adjustments" },
  { id: "external-tuition", label: "External Tuition" },
  { id: "deductions", label: "Deductions" },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "Active":
      return "Current";
    case "On Leave":
      return "Pending";
    case "Terminated":
      return "Archived";
    default:
      return "Archived";
  }
};

const dateOnly = (value?: string) => value ?? new Date().toISOString().split("T")[0] ?? "";

export function PayrollEmployeeDetailPage({
  employee,
  currentPeriod,
  backHref,
  routeLoading = false,
  routeError = false,
  onPersistPosition,
  onPersistStipend,
  onPersistAdjustment,
  onPersistExternalTuitionPayment,
  onPersistDeduction,
  onPersistEmployeeProfile,
}: PayrollEmployeeDetailPageProps) {
  const [employeeState, setEmployeeState] = useState(employee);
  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const [positions, setPositions] = useState<PayrollUiPosition[]>(
    employee.positions,
  );
  const [stipends, setStipends] = useState<PayrollUiStipend[]>(employee.stipends);
  const [adjustments, setAdjustments] = useState<PayrollUiAdjustment[]>(
    employee.adjustments,
  );
  const [externalTuitionPayments, setExternalTuitionPayments] = useState<
    PayrollUiExternalTuitionPayment[]
  >(employee.externalTuitionPayments);
  const [deductions, setDeductions] = useState<PayrollUiDeduction[]>(
    employee.deductions,
  );

  const [isPositionFormOpen, setIsPositionFormOpen] = useState(false);
  const [isStipendFormOpen, setIsStipendFormOpen] = useState(false);
  const [isAdjustmentFormOpen, setIsAdjustmentFormOpen] = useState(false);
  const [isExternalTuitionFormOpen, setIsExternalTuitionFormOpen] =
    useState(false);
  const [isDeductionFormOpen, setIsDeductionFormOpen] = useState(false);
  const [isLeaveManagementOpen, setIsLeaveManagementOpen] = useState(false);

  const [adjustmentVariant, setAdjustmentVariant] = useState<
    "quick" | "structured"
  >("quick");
  const [editingPosition, setEditingPosition] =
    useState<PayrollUiPosition | null>(null);
  const [editingStipend, setEditingStipend] =
    useState<PayrollUiStipend | null>(null);
  const [editingAdjustment, setEditingAdjustment] =
    useState<PayrollUiAdjustment | null>(null);
  const [editingExternalTuition, setEditingExternalTuition] =
    useState<PayrollUiExternalTuitionPayment | null>(null);
  const [editingDeduction, setEditingDeduction] =
    useState<PayrollUiDeduction | null>(null);

  const [activePositionMenu, setActivePositionMenu] = useState<string | null>(null);
  const [activeStipendMenu, setActiveStipendMenu] = useState<string | null>(null);
  const [activeAdjustmentMenu, setActiveAdjustmentMenu] = useState<string | null>(null);
  const [activeExternalTuitionMenu, setActiveExternalTuitionMenu] =
    useState<string | null>(null);
  const [activeDeductionMenu, setActiveDeductionMenu] = useState<string | null>(null);

  const [positionMenuPosition, setPositionMenuPosition] = useState({ top: 0, left: 0 });
  const [stipendMenuPosition, setStipendMenuPosition] = useState({ top: 0, left: 0 });
  const [adjustmentMenuPosition, setAdjustmentMenuPosition] = useState({ top: 0, left: 0 });
  const [externalTuitionMenuPosition, setExternalTuitionMenuPosition] =
    useState({ top: 0, left: 0 });
  const [deductionMenuPosition, setDeductionMenuPosition] = useState({ top: 0, left: 0 });

  const positionMenuRef = useRef<HTMLDivElement>(null);
  const stipendMenuRef = useRef<HTMLDivElement>(null);
  const adjustmentMenuRef = useRef<HTMLDivElement>(null);
  const externalTuitionMenuRef = useRef<HTMLDivElement>(null);
  const deductionMenuRef = useRef<HTMLDivElement>(null);

  const hydrateEmployee = (nextEmployee: PayrollUiEmployee) => {
    setEmployeeState(nextEmployee);
    setPositions(nextEmployee.positions);
    setStipends(nextEmployee.stipends);
    setAdjustments(nextEmployee.adjustments);
    setExternalTuitionPayments(nextEmployee.externalTuitionPayments);
    setDeductions(nextEmployee.deductions);
  };

  useEffect(() => {
    hydrateEmployee(employee);
  }, [employee]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        positionMenuRef.current &&
        !positionMenuRef.current.contains(event.target as Node)
      ) {
        setActivePositionMenu(null);
      }
      if (
        stipendMenuRef.current &&
        !stipendMenuRef.current.contains(event.target as Node)
      ) {
        setActiveStipendMenu(null);
      }
      if (
        adjustmentMenuRef.current &&
        !adjustmentMenuRef.current.contains(event.target as Node)
      ) {
        setActiveAdjustmentMenu(null);
      }
      if (
        externalTuitionMenuRef.current &&
        !externalTuitionMenuRef.current.contains(event.target as Node)
      ) {
        setActiveExternalTuitionMenu(null);
      }
      if (
        deductionMenuRef.current &&
        !deductionMenuRef.current.contains(event.target as Node)
      ) {
        setActiveDeductionMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calculateMenuPosition = (buttonElement: HTMLElement) => {
    const rect = buttonElement.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY + 4,
      left: rect.right + window.scrollX - 160,
    };
  };

  const currentPeriodAdjustments = adjustments.filter(
    (adjustment) => adjustment.periodId === currentPeriod.id,
  );
  const currentPeriodDeductions = deductions.filter(
    (deduction) => deduction.periodId === currentPeriod.id,
  );
  const grossTotal =
    employeeState.annualCompTotal / 26 +
    stipends.reduce((sum, stipend) => sum + stipend.totalAmount / 20, 0) +
    currentPeriodAdjustments.reduce((sum, adjustment) => sum + adjustment.amount, 0);
  const totalDeductions = currentPeriodDeductions.reduce(
    (sum, deduction) => sum + deduction.amount,
    0,
  );

  const applyPersistedEmployee = (next?: PayrollUiEmployee | void) => {
    if (next) {
      hydrateEmployee(next);
    }
  };

  const appendLocalPosition = (draft: PayrollPositionDraft) => {
    const nextPosition: PayrollUiPosition = {
      id: draft.id ?? `pos-${Date.now()}`,
      employeeId: employeeState.id,
      title: draft.title ?? "New Position",
      track: draft.track ?? "General Studies",
      compensationType: draft.compensationType ?? "Annual Salary",
      amount: draft.amount ?? 0,
      ftePercentage: draft.ftePercentage ?? 100,
      payDistribution: draft.payDistribution ?? "Spread Evenly",
      effectiveDate: draft.effectiveDate ?? currentPeriod.startDate,
      endDate: draft.endDate,
      parsonageEnabled: draft.parsonageEnabled,
      parsonageAmount: draft.parsonageAmount,
    };
    setPositions((current) => {
      if (editingPosition) {
        return current.map((position) =>
          position.id === editingPosition.id ? nextPosition : position,
        );
      }
      return [nextPosition, ...current];
    });
  };

  const appendLocalStipend = (draft: PayrollStipendDraft) => {
    const nextStipend: PayrollUiStipend = {
      id: draft.id ?? `stip-${Date.now()}`,
      employeeId: employeeState.id,
      type: draft.type ?? "Custom stipend",
      totalAmount: draft.totalAmount ?? 0,
      paymentSchedule: draft.paymentSchedule ?? "Spread Evenly Across Pay Periods",
      effectiveDate: draft.effectiveDate ?? currentPeriod.startDate,
      endDate: draft.endDate ?? currentPeriod.endDate,
      notes: draft.notes,
      scheduledPeriodId: draft.scheduledPeriodId,
      distributionPeriodIds: draft.distributionPeriodIds,
    };
    setStipends((current) => {
      if (editingStipend) {
        return current.map((stipend) =>
          stipend.id === editingStipend.id ? nextStipend : stipend,
        );
      }
      return [nextStipend, ...current];
    });
  };

  const appendLocalAdjustment = (draft: PayrollAdjustmentDraft) => {
    const nextAdjustment: PayrollUiAdjustment = {
      id: draft.id ?? `adj-${Date.now()}`,
      employeeId: employeeState.id,
      periodId: currentPeriod.id,
      type: draft.type ?? "Additional Pay",
      amount: draft.amount ?? 0,
      description: draft.description ?? "Adjustment",
      timestamp: draft.timestamp ?? new Date().toISOString(),
    };
    setAdjustments((current) => {
      if (editingAdjustment) {
        return current.map((adjustment) =>
          adjustment.id === editingAdjustment.id ? nextAdjustment : adjustment,
        );
      }
      return [nextAdjustment, ...current];
    });
  };

  const appendLocalExternalTuition = (draft: PayrollExternalTuitionDraft) => {
    const nextPayment: PayrollUiExternalTuitionPayment = {
      id: draft.id ?? `ext-${Date.now()}`,
      employeeId: employeeState.id,
      receivingSchoolName: draft.receivingSchoolName ?? "",
      childName: draft.childName ?? "",
      annualAmount: draft.annualAmount ?? 0,
      taxTreatment: draft.taxTreatment ?? "Tax-Exempt",
      notes: draft.notes,
    };
    setExternalTuitionPayments((current) => {
      if (editingExternalTuition) {
        return current.map((payment) =>
          payment.id === editingExternalTuition.id ? nextPayment : payment,
        );
      }
      return [nextPayment, ...current];
    });
  };

  const appendLocalDeduction = (draft: PayrollDeductionDraft) => {
    const nextDeduction: PayrollUiDeduction = {
      id: draft.id ?? `ded-${Date.now()}`,
      employeeId: employeeState.id,
      periodId: currentPeriod.id,
      deductionId: draft.deductionId,
      deductionType: draft.deductionType ?? "Health Insurance",
      customTypeName: draft.customTypeName,
      taxClassification: draft.taxClassification ?? "Pre-Tax",
      amount: draft.amount ?? 0,
      isRecurring: draft.isRecurring ?? true,
    };
    setDeductions((current) => {
      if (editingDeduction) {
        return current.map((deduction) =>
          deduction.id === editingDeduction.id ? nextDeduction : deduction,
        );
      }
      return [nextDeduction, ...current];
    });
  };

  const handleReturnFromLeave = async () => {
    const nextEmployee =
      (await onPersistEmployeeProfile?.({
        status: "Active",
        leaveInfo: null,
      })) || undefined;
    if (nextEmployee) {
      hydrateEmployee(nextEmployee);
      return;
    }
    setEmployeeState((current) => ({
      ...current,
      status: "Active",
      leaveInfo: undefined,
    }));
  };

  const ActionMenu = ({
    isOpen,
    position,
    menuRef,
    onEdit,
    onDuplicate,
    onDelete,
  }: {
    isOpen: boolean;
    position: { top: number; left: number };
    menuRef: React.RefObject<HTMLDivElement>;
    onEdit: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
  }) => {
    if (!isOpen) {
      return null;
    }
    return createPortal(
      <div
        ref={menuRef}
        className="fixed z-[9999] w-40 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <button
          onClick={onEdit}
          className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
        >
          Edit
        </button>
        <button
          onClick={onDuplicate}
          className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
        >
          Duplicate
        </button>
        <div className="my-1 h-px bg-border" />
        <button
          onClick={onDelete}
          className="block w-full px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
        >
          Delete
        </button>
      </div>,
      document.body,
    );
  };

  return (
    <div
      data-testid="payroll-employee-detail-page-root"
      data-route-ready={!routeLoading && !routeError ? "true" : "false"}
      data-route-loading={routeLoading ? "true" : "false"}
      data-route-error={routeError ? "true" : "false"}
      className="min-h-full bg-background-light p-6 font-body"
    >
      <div className="mx-auto max-w-[1440px]">
        <Link
          to={backHref}
          className="mb-6 inline-flex items-center gap-2 font-body text-sm font-medium text-text-secondary transition-colors hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to roster
        </Link>

        <div className="mb-6 rounded-[12px] border border-border bg-surface-light p-6 shadow-sm">
          <div className="flex min-w-0 flex-col justify-between gap-6 md:flex-row md:items-start">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex min-w-0 items-center gap-3">
                <h1 className="min-w-0 font-heading text-2xl font-semibold text-iron-grey">
                  {employeeState.firstName} {employeeState.lastName}
                </h1>
                <StatusBadge
                  status={getStatusBadgeVariant(employeeState.status) as any}
                  size="md"
                />
              </div>
              <p className="flex min-w-0 items-center gap-2 font-body text-sm text-text-secondary">
                <span>ID: {employeeState.employeeId}</span>
                <span>•</span>
                <span>{employeeState.employmentType}</span>
                <span>•</span>
                <span>Track: {employeeState.track}</span>
                {employeeState.email ? (
                  <>
                    <span>•</span>
                    <span>{employeeState.email}</span>
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex min-w-0 flex-wrap gap-8">
              <div>
                <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                  Positions
                </p>
                <p className="font-body text-xl font-semibold text-iron-grey">
                  {positions.length}
                </p>
              </div>
              <div>
                <p className="mb-1 font-body text-xs uppercase tracking-wider text-text-secondary">
                  Annual Comp
                </p>
                <p className="font-body text-xl font-semibold text-primary">
                  {employeeState.employmentType === "Hourly"
                    ? "Varies"
                    : formatCurrency(employeeState.annualCompTotal)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {employeeState.status === "On Leave" && employeeState.leaveInfo ? (
          <div className="mb-6 flex flex-col gap-4 rounded-[8px] border-l-4 border-coral bg-warning-background p-4 sm:flex-row sm:items-start">
            <AlertCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-coral" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <p className="font-body text-sm font-semibold text-iron-grey">
                    {employeeState.leaveInfo.leaveType} Leave
                  </p>
                  <p className="mt-0.5 font-body text-xs text-text-secondary">
                    {new Date(
                      employeeState.leaveInfo.startDate,
                    ).toLocaleDateString()}{" "}
                    - Expected return:{" "}
                    {new Date(
                      employeeState.leaveInfo.expectedReturnDate,
                    ).toLocaleDateString()}
                  </p>
                </div>
                {employeeState.leaveInfo.partialPayPercentage > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="font-body text-xs uppercase tracking-wider text-text-secondary">
                      Partial Pay:
                    </span>
                    <span className="font-body text-sm font-semibold text-iron-grey">
                      {employeeState.leaveInfo.partialPayPercentage}%
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIsLeaveManagementOpen(true)}
                className="whitespace-nowrap rounded-[6px] border border-border bg-surface-light px-3 py-1.5 font-body text-xs font-medium text-iron-grey transition-colors hover:bg-muted"
              >
                Update Leave Status
              </button>
              <button
                onClick={() => void handleReturnFromLeave()}
                className="whitespace-nowrap rounded-[6px] bg-primary px-3 py-1.5 font-body text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
              >
                Return from Leave
              </button>
            </div>
          </div>
        ) : null}

        <div className="mb-6 border-b border-border">
          <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
            {DETAIL_TABS.filter(
              (tab) => tab.id !== "time" || employeeState.employmentType === "Hourly",
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                data-testid={`payroll-detail-tab-${tab.id}`}
                data-state={activeTab === tab.id ? "active" : "inactive"}
                className={`yst-motion-color inline-flex border-b-2 pb-3 font-body text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary font-bold text-primary"
                    : "border-transparent font-medium text-text-secondary hover:text-iron-grey"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="animate-in fade-in duration-200">
          {activeTab === "overview" ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-[12px] border border-border bg-surface-light p-6 shadow-sm">
                <h3 className="mb-4 font-title text-lg font-semibold text-iron-grey">
                  Current Period Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-border border-dashed pb-3">
                    <span className="font-body text-sm text-text-secondary">
                      Base Pay
                    </span>
                    <span className="font-body text-sm font-medium text-iron-grey">
                      {formatCurrency(employeeState.annualCompTotal / 26)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-border border-dashed pb-3">
                    <span className="font-body text-sm text-text-secondary">
                      Stipends
                    </span>
                    <span className="font-body text-sm font-medium text-iron-grey">
                      {formatCurrency(
                        stipends.reduce((sum, stipend) => sum + stipend.totalAmount / 20, 0),
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-border border-dashed pb-3">
                    <span className="font-body text-sm text-text-secondary">
                      Adjustments
                    </span>
                    <span className="font-body text-sm font-medium text-iron-grey">
                      {formatCurrency(
                        currentPeriodAdjustments.reduce(
                          (sum, adjustment) => sum + adjustment.amount,
                          0,
                        ),
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-body text-sm font-bold text-iron-grey">
                      Gross Total
                    </span>
                    <span className="font-body text-lg font-bold text-primary">
                      {formatCurrency(grossTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "positions" ? (
            <div className="flex flex-col overflow-visible rounded-[12px] border border-border bg-surface-light shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                <h2 className="font-title text-lg font-semibold text-iron-grey">
                  Position History
                </h2>
                <button
                  onClick={() => {
                    setEditingPosition(null);
                    setIsPositionFormOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Position
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Title
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Track
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        FTE
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Effective
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {positions.map((position) => (
                      <Fragment key={position.id}>
                        <tr className="transition-colors hover:bg-[#fbf9fa]">
                          <td className="px-6 py-4 font-body text-sm font-medium text-iron-grey">
                            {position.title}
                          </td>
                          <td className="px-6 py-4 font-body text-sm text-text-secondary">
                            {position.track}
                          </td>
                          <td className="px-6 py-4 font-body text-sm text-text-secondary">
                            {position.compensationType}
                          </td>
                          <td className="px-6 py-4 text-right font-body text-sm font-medium text-iron-grey">
                            {position.compensationType === "Hourly Rate"
                              ? `$${position.amount}/hr`
                              : formatCurrency(position.amount)}
                          </td>
                          <td className="px-6 py-4 text-center font-body text-sm text-text-secondary">
                            {position.ftePercentage}%
                          </td>
                          <td className="px-6 py-4 font-body text-sm text-text-secondary">
                            {new Date(position.effectiveDate).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={(event) => {
                                const newMenuId =
                                  activePositionMenu === position.id ? null : position.id;
                                setActivePositionMenu(newMenuId);
                                if (newMenuId) {
                                  setPositionMenuPosition(
                                    calculateMenuPosition(event.currentTarget),
                                  );
                                }
                              }}
                              className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                            >
                              <MoreVerticalIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>

                        {position.parsonageEnabled && position.parsonageAmount ? (
                          <tr className="bg-muted/20 transition-colors hover:bg-muted/30">
                            <td colSpan={7} className="px-6 py-3">
                              <div className="flex items-center justify-between pl-6">
                                <div className="flex items-center gap-2">
                                  <span className="font-body text-sm text-text-secondary">
                                    Parsonage
                                  </span>
                                  <span className="inline-flex items-center rounded-[6px] border border-primary/20 bg-primary/10 px-2 py-0.5 font-body text-[11px] font-medium text-primary">
                                    Tax Exempt
                                  </span>
                                </div>
                                <span className="font-body text-sm font-medium text-iron-grey">
                                  {formatCurrency(
                                    position.compensationType === "Monthly Salary"
                                      ? position.parsonageAmount / 12
                                      : position.parsonageAmount / 26,
                                  )}
                                  <span className="ml-1 text-xs text-text-tertiary">
                                    / period
                                  </span>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                    {positions.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-12 text-center font-body text-sm text-text-secondary"
                        >
                          No positions found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === "stipends" ? (
            <div className="flex flex-col overflow-visible rounded-[12px] border border-border bg-surface-light shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                <h2 className="font-title text-lg font-semibold text-iron-grey">
                  Stipends
                </h2>
                <button
                  onClick={() => {
                    setEditingStipend(null);
                    setIsStipendFormOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Stipend
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Schedule
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {stipends.map((stipend) => (
                      <tr
                        key={stipend.id}
                        className="transition-colors hover:bg-[#fbf9fa]"
                      >
                        <td className="px-6 py-4 font-body text-sm font-medium text-iron-grey">
                          {stipend.type}
                        </td>
                        <td className="px-6 py-4 text-right font-body text-sm font-medium text-iron-grey">
                          {formatCurrency(stipend.totalAmount)}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-text-secondary">
                          {stipend.paymentSchedule}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-text-secondary">
                          {new Date(stipend.effectiveDate).toLocaleDateString()} -{" "}
                          {new Date(stipend.endDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(event) => {
                              const newMenuId =
                                activeStipendMenu === stipend.id ? null : stipend.id;
                              setActiveStipendMenu(newMenuId);
                              if (newMenuId) {
                                setStipendMenuPosition(
                                  calculateMenuPosition(event.currentTarget),
                                );
                              }
                            }}
                            className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                          >
                            <MoreVerticalIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {stipends.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-12 text-center font-body text-sm text-text-secondary"
                        >
                          No stipends assigned.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === "time" && employeeState.employmentType === "Hourly" ? (
            <TimeEntryGrid employees={[employeeState]} period={currentPeriod} />
          ) : null}

          {activeTab === "adjustments" ? (
            <div className="flex flex-col overflow-visible rounded-[12px] border border-border bg-surface-light shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                <h2 className="font-title text-lg font-semibold text-iron-grey">
                  Mid-Cycle Adjustments
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingAdjustment(null);
                      setAdjustmentVariant("structured");
                      setIsAdjustmentFormOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-[13px] font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted"
                  >
                    Change Request
                  </button>
                  <button
                    onClick={() => {
                      setEditingAdjustment(null);
                      setAdjustmentVariant("quick");
                      setIsAdjustmentFormOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Quick Add
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Date
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Type
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {adjustments.map((adjustment) => (
                      <tr
                        key={adjustment.id}
                        className="transition-colors hover:bg-[#fbf9fa]"
                      >
                        <td className="px-6 py-4 font-body text-sm text-text-secondary">
                          {new Date(adjustment.timestamp).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-iron-grey">
                          {adjustment.type}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-iron-grey">
                          {adjustment.description}
                        </td>
                        <td
                          className={`px-6 py-4 text-right font-body text-sm font-medium ${
                            adjustment.amount < 0 ? "text-strawberry" : "text-jungle"
                          }`}
                        >
                          {adjustment.amount > 0 ? "+" : ""}
                          {formatCurrency(adjustment.amount)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(event) => {
                              const newMenuId =
                                activeAdjustmentMenu === adjustment.id
                                  ? null
                                  : adjustment.id;
                              setActiveAdjustmentMenu(newMenuId);
                              if (newMenuId) {
                                setAdjustmentMenuPosition(
                                  calculateMenuPosition(event.currentTarget),
                                );
                              }
                            }}
                            className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                          >
                            <MoreVerticalIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {adjustments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-12 text-center font-body text-sm text-text-secondary"
                        >
                          No adjustments for this period.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === "external-tuition" ? (
            <div className="flex flex-col overflow-visible rounded-[12px] border border-border bg-surface-light shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                <h2 className="font-title text-lg font-semibold text-iron-grey">
                  External Tuition Payments
                </h2>
                <button
                  onClick={() => {
                    setEditingExternalTuition(null);
                    setIsExternalTuitionFormOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Payment
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Receiving School
                      </th>
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Child Name
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Annual Amount
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Per-Period
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Tax Treatment
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {externalTuitionPayments.map((payment) => (
                      <tr
                        key={payment.id}
                        className="transition-colors hover:bg-[#fbf9fa]"
                      >
                        <td className="px-6 py-4 font-body text-sm font-medium text-iron-grey">
                          {payment.receivingSchoolName}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-text-secondary">
                          {payment.childName}
                        </td>
                        <td className="px-6 py-4 text-right font-body text-sm font-medium text-iron-grey">
                          {formatCurrency(payment.annualAmount)}
                        </td>
                        <td className="px-6 py-4 text-right font-body text-sm text-text-secondary">
                          {formatCurrency(payment.annualAmount / 26)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center rounded-[6px] border px-2 py-1 font-body text-[11px] font-medium ${
                              payment.taxTreatment === "Tax-Exempt"
                                ? "border-aquamarine/20 bg-success-background text-jungle"
                                : "border-coral/20 bg-warning-background text-[#D97706]"
                            }`}
                          >
                            {payment.taxTreatment}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(event) => {
                              const newMenuId =
                                activeExternalTuitionMenu === payment.id
                                  ? null
                                  : payment.id;
                              setActiveExternalTuitionMenu(newMenuId);
                              if (newMenuId) {
                                setExternalTuitionMenuPosition(
                                  calculateMenuPosition(event.currentTarget),
                                );
                              }
                            }}
                            className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                          >
                            <MoreVerticalIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {externalTuitionPayments.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <p className="mb-4 font-body text-sm text-text-secondary">
                            No external tuition payments configured
                          </p>
                          <button
                            onClick={() => {
                              setEditingExternalTuition(null);
                              setIsExternalTuitionFormOpen(true);
                            }}
                            className="inline-flex items-center gap-2 rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Add Payment
                          </button>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === "deductions" ? (
            <div className="flex flex-col overflow-visible rounded-[12px] border border-border bg-surface-light shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 p-4">
                <h2 className="font-title text-lg font-semibold text-iron-grey">
                  Deductions
                </h2>
                <button
                  onClick={() => {
                    setEditingDeduction(null);
                    setIsDeductionFormOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-[6px] bg-primary px-3 py-2 font-body text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Deduction
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="px-6 py-3 font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Deduction Type
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Tax Classification
                      </th>
                      <th className="px-6 py-3 text-right font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Per-Period Amount
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Recurring
                      </th>
                      <th className="px-6 py-3 text-center font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {deductions.map((deduction) => (
                      <tr
                        key={deduction.id}
                        className="transition-colors hover:bg-[#fbf9fa]"
                      >
                        <td className="px-6 py-4 font-body text-sm font-medium text-iron-grey">
                          {deduction.deductionType === "Custom" && deduction.customTypeName
                            ? deduction.customTypeName
                            : deduction.deductionType}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center rounded-[6px] border px-2 py-1 font-body text-[11px] font-medium ${
                              deduction.taxClassification === "Pre-Tax"
                                ? "border-primary/20 bg-info-background text-primary"
                                : "border-border bg-muted text-text-secondary"
                            }`}
                          >
                            {deduction.taxClassification}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-body text-sm font-medium text-iron-grey">
                          {formatCurrency(deduction.amount)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {deduction.isRecurring ? (
                            <RepeatIcon className="mx-auto h-4 w-4 text-primary" />
                          ) : null}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(event) => {
                              const newMenuId =
                                activeDeductionMenu === deduction.id
                                  ? null
                                  : deduction.id;
                              setActiveDeductionMenu(newMenuId);
                              if (newMenuId) {
                                setDeductionMenuPosition(
                                  calculateMenuPosition(event.currentTarget),
                                );
                              }
                            }}
                            className="rounded-[6px] p-1 text-text-secondary transition-colors hover:bg-border/50 hover:text-iron-grey focus:outline-none"
                          >
                            <MoreVerticalIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {deductions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <p className="mb-4 font-body text-sm text-text-secondary">
                            No deductions entered for this period
                          </p>
                          <button
                            onClick={() => {
                              setEditingDeduction(null);
                              setIsDeductionFormOpen(true);
                            }}
                            className="inline-flex items-center gap-2 rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Add Deduction
                          </button>
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {deductions.length > 0 ? (
                <div className="border-t border-border bg-muted/20 p-4">
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-4">
                      <span className="font-body text-sm font-medium text-iron-grey">
                        Total Deductions:
                      </span>
                      <span className="font-body text-lg font-bold text-strawberry">
                        {formatCurrency(totalDeductions)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-body text-sm text-text-secondary">
                        Net Pay Estimate:
                      </span>
                      <span className="font-body text-base font-semibold text-text-secondary">
                        {formatCurrency(grossTotal - totalDeductions)}
                        <span className="ml-1 font-body text-xs text-text-tertiary">
                          (estimate)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {positions.find((position) => position.id === activePositionMenu) ? (
        <ActionMenu
          isOpen={Boolean(activePositionMenu)}
          position={positionMenuPosition}
          menuRef={positionMenuRef}
          onEdit={() => {
            setEditingPosition(
              positions.find((position) => position.id === activePositionMenu) ?? null,
            );
            setIsPositionFormOpen(true);
            setActivePositionMenu(null);
          }}
          onDuplicate={() => {
            const target = positions.find((position) => position.id === activePositionMenu);
            if (!target) {
              return;
            }
            const duplicate = {
              ...target,
              id: `pos-${Date.now()}`,
              effectiveDate: dateOnly(),
            };
            setPositions((current) => [duplicate, ...current]);
            setActivePositionMenu(null);
          }}
          onDelete={() => {
            setPositions((current) =>
              current.filter((position) => position.id !== activePositionMenu),
            );
            setActivePositionMenu(null);
          }}
        />
      ) : null}

      {stipends.find((stipend) => stipend.id === activeStipendMenu) ? (
        <ActionMenu
          isOpen={Boolean(activeStipendMenu)}
          position={stipendMenuPosition}
          menuRef={stipendMenuRef}
          onEdit={() => {
            setEditingStipend(
              stipends.find((stipend) => stipend.id === activeStipendMenu) ?? null,
            );
            setIsStipendFormOpen(true);
            setActiveStipendMenu(null);
          }}
          onDuplicate={() => {
            const target = stipends.find((stipend) => stipend.id === activeStipendMenu);
            if (!target) {
              return;
            }
            const duplicate = {
              ...target,
              id: `stip-${Date.now()}`,
              effectiveDate: dateOnly(),
            };
            setStipends((current) => [duplicate, ...current]);
            setActiveStipendMenu(null);
          }}
          onDelete={() => {
            setStipends((current) =>
              current.filter((stipend) => stipend.id !== activeStipendMenu),
            );
            setActiveStipendMenu(null);
          }}
        />
      ) : null}

      {adjustments.find((adjustment) => adjustment.id === activeAdjustmentMenu) ? (
        <ActionMenu
          isOpen={Boolean(activeAdjustmentMenu)}
          position={adjustmentMenuPosition}
          menuRef={adjustmentMenuRef}
          onEdit={() => {
            setEditingAdjustment(
              adjustments.find((adjustment) => adjustment.id === activeAdjustmentMenu) ??
                null,
            );
            setAdjustmentVariant("quick");
            setIsAdjustmentFormOpen(true);
            setActiveAdjustmentMenu(null);
          }}
          onDuplicate={() => {
            const target = adjustments.find(
              (adjustment) => adjustment.id === activeAdjustmentMenu,
            );
            if (!target) {
              return;
            }
            const duplicate = {
              ...target,
              id: `adj-${Date.now()}`,
              timestamp: new Date().toISOString(),
            };
            setAdjustments((current) => [duplicate, ...current]);
            setActiveAdjustmentMenu(null);
          }}
          onDelete={() => {
            setAdjustments((current) =>
              current.filter((adjustment) => adjustment.id !== activeAdjustmentMenu),
            );
            setActiveAdjustmentMenu(null);
          }}
        />
      ) : null}

      {externalTuitionPayments.find(
        (payment) => payment.id === activeExternalTuitionMenu,
      )
        ? createPortal(
            <div
              ref={externalTuitionMenuRef}
              className="fixed z-[9999] w-40 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100"
              style={{
                top: `${externalTuitionMenuPosition.top}px`,
                left: `${externalTuitionMenuPosition.left}px`,
              }}
            >
              <button
                onClick={() => {
                  setEditingExternalTuition(
                    externalTuitionPayments.find(
                      (payment) => payment.id === activeExternalTuitionMenu,
                    ) ?? null,
                  );
                  setIsExternalTuitionFormOpen(true);
                  setActiveExternalTuitionMenu(null);
                }}
                className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
              >
                Edit
              </button>
              <div className="my-1 h-px bg-border" />
              <button
                onClick={() => {
                  setExternalTuitionPayments((current) =>
                    current.filter(
                      (payment) => payment.id !== activeExternalTuitionMenu,
                    ),
                  );
                  setActiveExternalTuitionMenu(null);
                }}
                className="block w-full px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
              >
                Remove
              </button>
            </div>,
            document.body,
          )
        : null}

      {deductions.find((deduction) => deduction.id === activeDeductionMenu)
        ? createPortal(
            <div
              ref={deductionMenuRef}
              className="fixed z-[9999] w-40 rounded-[8px] border border-border bg-surface-light py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100"
              style={{
                top: `${deductionMenuPosition.top}px`,
                left: `${deductionMenuPosition.left}px`,
              }}
            >
              <button
                onClick={() => {
                  setEditingDeduction(
                    deductions.find(
                      (deduction) => deduction.id === activeDeductionMenu,
                    ) ?? null,
                  );
                  setIsDeductionFormOpen(true);
                  setActiveDeductionMenu(null);
                }}
                className="block w-full px-4 py-2 text-left font-body text-[13px] text-iron-grey transition-colors hover:bg-muted"
              >
                Edit
              </button>
              <div className="my-1 h-px bg-border" />
              <button
                onClick={() => {
                  setDeductions((current) =>
                    current.filter((deduction) => deduction.id !== activeDeductionMenu),
                  );
                  setActiveDeductionMenu(null);
                }}
                className="block w-full px-4 py-2 text-left font-body text-[13px] text-strawberry transition-colors hover:bg-error-background"
              >
                Remove
              </button>
            </div>,
            document.body,
          )
        : null}

      <PositionForm
        isOpen={isPositionFormOpen}
        onClose={() => {
          setIsPositionFormOpen(false);
          setEditingPosition(null);
        }}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        initialValue={editingPosition}
        onSave={async (draft) => {
          const next = await onPersistPosition?.(draft);
          applyPersistedEmployee(next);
          if (!next) {
            appendLocalPosition(draft);
          }
        }}
      />

      <StipendForm
        isOpen={isStipendFormOpen}
        onClose={() => {
          setIsStipendFormOpen(false);
          setEditingStipend(null);
        }}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        initialValue={editingStipend}
        onSave={async (draft) => {
          const next = await onPersistStipend?.(draft);
          applyPersistedEmployee(next);
          if (!next) {
            appendLocalStipend(draft);
          }
        }}
      />

      <AdjustmentForm
        isOpen={isAdjustmentFormOpen}
        onClose={() => {
          setIsAdjustmentFormOpen(false);
          setEditingAdjustment(null);
        }}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        variant={adjustmentVariant}
        initialValue={editingAdjustment}
        onSave={async (draft) => {
          const next = await onPersistAdjustment?.(draft);
          applyPersistedEmployee(next);
          if (!next) {
            appendLocalAdjustment(draft);
          }
        }}
      />

      <ExternalTuitionPaymentForm
        isOpen={isExternalTuitionFormOpen}
        onClose={() => {
          setIsExternalTuitionFormOpen(false);
          setEditingExternalTuition(null);
        }}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        initialValue={editingExternalTuition}
        onSave={async (draft) => {
          const next = await onPersistExternalTuitionPayment?.(draft);
          applyPersistedEmployee(next);
          if (!next) {
            appendLocalExternalTuition(draft);
          }
        }}
      />

      <DeductionForm
        isOpen={isDeductionFormOpen}
        onClose={() => {
          setIsDeductionFormOpen(false);
          setEditingDeduction(null);
        }}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        initialValue={editingDeduction}
        onSave={async (draft) => {
          const next = await onPersistDeduction?.(draft);
          applyPersistedEmployee(next);
          if (!next) {
            appendLocalDeduction(draft);
          }
        }}
      />

      <LeaveManagementModal
        isOpen={isLeaveManagementOpen}
        onClose={() => setIsLeaveManagementOpen(false)}
        employeeName={`${employeeState.firstName} ${employeeState.lastName}`}
        currentLeaveInfo={employeeState.leaveInfo}
        onSave={async (leaveInfo) => {
          const next = await onPersistEmployeeProfile?.({
            status: "On Leave",
            leaveInfo,
          });
          if (next) {
            hydrateEmployee(next);
            return;
          }
          setEmployeeState((current) => ({
            ...current,
            status: "On Leave",
            leaveInfo,
          }));
        }}
      />
    </div>
  );
}
